import { beginWebAudioCapture } from "./webAudio";

export type WebSpeechAvailability = "supported" | "unsupported";
export type WebMicrophoneAvailability = "available" | "missing" | "unknown";

export interface WebSpeechAlternative {
  transcript: string;
  confidence: number | null;
}

export interface WebSpeechResult {
  transcript: string;
  confidence: number | null;
  alternatives: WebSpeechAlternative[];
}

export class WebSpeechError extends Error {
  readonly code: string;

  constructor(code: string, message: string = code) {
    super(message);
    this.name = "WebSpeechError";
    this.code = code;
  }
}

export function getWebSpeechAvailability(): WebSpeechAvailability {
  if (typeof window === "undefined") {
    return "unsupported";
  }
  return window.SpeechRecognition || window.webkitSpeechRecognition
    ? "supported"
    : "unsupported";
}

export async function getWebMicrophoneAvailability(): Promise<WebMicrophoneAvailability> {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.enumerateDevices
  ) {
    return "unknown";
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    if (devices.some((device) => device.kind === "audioinput")) {
      return "available";
    }
    if (!navigator.permissions?.query) {
      return "unknown";
    }
    try {
      const permission = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      return permission.state === "granted" ? "missing" : "unknown";
    } catch {
      return "unknown";
    }
  } catch {
    return "unknown";
  }
}

function microphoneError(error: unknown): WebSpeechError {
  const name =
    error instanceof DOMException
      ? error.name
      : error && typeof error === "object" && "name" in error
      ? String(error.name)
      : "";
  const message = error instanceof Error ? error.message : String(error);

  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return new WebSpeechError("speech_microphone_not_found", message);
  }
  if (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    name === "SecurityError"
  ) {
    return new WebSpeechError("speech_microphone_permission_denied", message);
  }
  if (
    name === "NotReadableError" ||
    name === "TrackStartError" ||
    name === "AbortError"
  ) {
    return new WebSpeechError("speech_microphone_unavailable", message);
  }
  return new WebSpeechError("speech_microphone_unavailable", message);
}

async function assertWebMicrophoneReady(): Promise<void> {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.getUserMedia
  ) {
    return;
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (error) {
    throw microphoneError(error);
  }
  stream.getTracks().forEach((track) => track.stop());
}

function recognitionErrorCode(error: string): string {
  if (error === "audio-capture") return "speech_microphone_unavailable";
  if (error === "not-allowed" || error === "service-not-allowed") {
    return "speech_microphone_permission_denied";
  }
  return `speech_${error || "failed"}`;
}

export async function recognizeSpeechOnce(
  locale: string,
  timeoutMs = 12000,
  signal?: AbortSignal
): Promise<WebSpeechResult> {
  if (typeof window === "undefined") {
    throw new WebSpeechError("speech_unavailable");
  }

  const Recognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!Recognition) {
    throw new WebSpeechError("speech_unavailable");
  }

  if (signal?.aborted) throw new WebSpeechError("speech_cancelled");
  const releaseCapture = beginWebAudioCapture();
  if (!releaseCapture) throw new WebSpeechError("speech_capture_active");

  return new Promise((resolve, reject) => {
    let recognition: SpeechRecognition | null = null;
    let settled = false;
    let receivedResult = false;
    let timeoutId: number | null = null;

    const finish = (action: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      signal?.removeEventListener("abort", onAbort);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      try {
        recognition?.abort();
      } catch {
        /* Already stopped by the browser. */
      }
      releaseCapture();
      action();
    };
    const onAbort = () =>
      finish(() => reject(new WebSpeechError("speech_cancelled")));
    signal?.addEventListener("abort", onAbort, { once: true });

    const armListeningTimeout = () => {
      if (settled || timeoutId !== null) {
        return;
      }
      timeoutId = window.setTimeout(() => {
        finish(() => reject(new WebSpeechError("speech_timeout")));
      }, timeoutMs);
    };

    void assertWebMicrophoneReady()
      .then(() => {
        if (settled) return;
        recognition = new Recognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = locale;
        recognition.maxAlternatives = 10;
        recognition.onstart = armListeningTimeout;
        recognition.onresult = (event) => {
          const speechResult = event.results.item(0);
          const alternativeCount =
            Number.isFinite(speechResult?.length) && speechResult.length > 0
              ? speechResult.length
              : 1;
          const alternatives: WebSpeechAlternative[] = [];
          for (let index = 0; index < alternativeCount; index += 1) {
            const alternative = speechResult?.item(index);
            const transcript = alternative?.transcript?.trim() || "";
            if (
              !transcript ||
              alternatives.some((entry) => entry.transcript === transcript)
            ) {
              continue;
            }
            alternatives.push({
              transcript,
              confidence: Number.isFinite(alternative?.confidence)
                ? alternative.confidence
                : null,
            });
          }
          const primary = alternatives[0];
          receivedResult = Boolean(primary?.transcript);
          finish(() =>
            resolve({
              transcript: primary?.transcript || "",
              confidence: primary?.confidence ?? null,
              alternatives,
            })
          );
        };
        recognition.onerror = (event) => {
          finish(() =>
            reject(
              new WebSpeechError(
                recognitionErrorCode(event.error),
                event.message
              )
            )
          );
        };
        recognition.onend = () => {
          if (!receivedResult) {
            finish(() => reject(new WebSpeechError("speech_no_match")));
          }
        };

        try {
          recognition.start();
        } catch (error) {
          finish(() =>
            reject(
              new WebSpeechError(
                "speech_start_failed",
                error instanceof Error ? error.message : String(error)
              )
            )
          );
        }
      })
      .catch((error) => finish(() => reject(error)));
  });
}
